import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import Sidebar from '../components/Sidebar';
import TimeLiterarioAtivo from '../components/TimeLiterarioAtivo';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Sparkles, Upload, FileText, BookOpen, Expand, ListTree, RefreshCw,
  ChevronRight, Loader2, CheckCircle2, Zap, Crown, AlertCircle,
  Eye, Edit, Save, Printer, Copy, X, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Writing modes configuration
const WRITING_MODES = [
  { id: 'ebook', label: 'Escrever Ebook', icon: FileText, description: 'Crie um ebook completo' },
  { id: 'livro', label: 'Escrever Livro', icon: BookOpen, description: 'Desenvolva um livro' },
  { id: 'expandir', label: 'Expandir Rascunho', icon: Expand, description: 'Amplie suas ideias' },
  { id: 'topicos', label: 'Tópicos em Texto', icon: ListTree, description: 'Transforme notas em prosa' },
  { id: 'reescrever', label: 'Reescrever no Meu Estilo', icon: RefreshCw, description: 'Adapte ao seu estilo' },
];

// Intensity levels
const INTENSITIES = [
  { id: 'fiel', label: 'Fiel', description: 'Máxima fidelidade ao seu estilo', free: true },
  { id: 'equilibrado', label: 'Equilibrado', description: 'Balance entre estilo e clareza', free: false },
  { id: 'elite', label: 'Elite', description: 'Versão elevada do seu estilo', free: false },
];

export default function EmuladorAcelerador() {
  const navigate = useNavigate();
  
  // State
  const [emulatorEnabled, setEmulatorEnabled] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // DNA State
  const [writingSamples, setWritingSamples] = useState('');
  const [hasDNA, setHasDNA] = useState(false);
  const [hasStyleGuide, setHasStyleGuide] = useState(false);
  
  // Generation State
  const [selectedMode, setSelectedMode] = useState('ebook');
  const [selectedIntensity, setSelectedIntensity] = useState('fiel');
  const [briefing, setBriefing] = useState({
    titulo: '',
    tema: '',
    publico_alvo: '',
    objetivo: '',
    pontos_obrigatorios: '',
    informacoes_adicionais: ''
  });
  
  // Result State
  const [generatedContent, setGeneratedContent] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
    loadWritingProfile();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai/emulator/settings`, { withCredentials: true });
      setEmulatorEnabled(response.data.emulator_enabled);
      setSelectedIntensity(response.data.emulator_intensity || 'fiel');
      setIsPremium(response.data.is_premium);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadWritingProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai/dna/my-profile`, { withCredentials: true });
      setHasDNA(response.data.has_dna);
      setHasStyleGuide(response.data.has_style_guide);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleToggleEmulator = async (enabled) => {
    setEmulatorEnabled(enabled);
    try {
      await axios.post(`${API_URL}/api/ai/emulator/settings`, {
        enabled,
        intensity: selectedIntensity
      }, { withCredentials: true });
    } catch (error) {
      toast.error('Erro ao atualizar configurações');
    }
  };

  const handleAnalyzeDNA = async () => {
    if (!writingSamples.trim()) {
      toast.error('Cole pelo menos um texto para análise');
      return;
    }

    setAnalyzing(true);
    try {
      // Split by separator if multiple samples
      const texts = writingSamples.split('---').map(t => t.trim()).filter(t => t.length > 0);
      
      // Analyze DNA
      const dnaResponse = await axios.post(`${API_URL}/api/ai/dna/analyze`, {
        texts
      }, { withCredentials: true });

      // Generate Style Guide
      await axios.post(`${API_URL}/api/ai/dna/style-guide`, {
        dna_analysis: dnaResponse.data.analysis
      }, { withCredentials: true });

      setHasDNA(true);
      setHasStyleGuide(true);
      toast.success('DNA da Escrita identificado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao analisar DNA');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    if (!briefing.titulo || !briefing.tema) {
      toast.error('Preencha pelo menos o título e o tema');
      return;
    }

    // Check premium for advanced intensities
    if (!isPremium && (selectedIntensity === 'equilibrado' || selectedIntensity === 'elite')) {
      toast.error('Intensidades avançadas são exclusivas para Premium');
      return;
    }

    setGenerating(true);
    setShowResult(false);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/generate`, {
        mode: selectedMode,
        intensity: selectedIntensity,
        ...briefing
      }, { withCredentials: true });

      setGeneratedContent(response.data.content);
      setEditableContent(response.data.content);
      setShowResult(true);
      toast.success('Conteúdo gerado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar conteúdo');
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenInEditor = () => {
    // Store content in localStorage for the editor to pick up
    localStorage.setItem('emulator_generated_content', editableContent);
    localStorage.setItem('emulator_content_title', briefing.titulo);
    toast.success('Conteúdo pronto para o editor!');
    navigate('/novo-livro', { state: { fromEmulator: true, title: briefing.titulo } });
  };

  const handleOpenContentModal = () => {
    setEditableContent(generatedContent);
    setIsEditing(false);
    setShowContentModal(true);
  };

  const handleSaveEditedContent = () => {
    setGeneratedContent(editableContent);
    setIsEditing(false);
    toast.success('Conteúdo salvo!');
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(editableContent);
    toast.success('Conteúdo copiado!');
  };

  const handlePrintContent = () => {
    const sanitizedTitle = DOMPurify.sanitize(briefing.titulo || 'Conteúdo Gerado');
    const sanitizedContent = DOMPurify.sanitize(editableContent);
    
    // Criar HTML seguro via Blob (evita document.write)
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${sanitizedTitle}</title>
          <style>
            body { 
              font-family: Georgia, serif; 
              padding: 40px; 
              max-width: 800px; 
              margin: 0 auto;
              line-height: 1.8;
            }
            h1 { color: #d97706; margin-bottom: 20px; }
            pre { white-space: pre-wrap; font-family: Georgia, serif; }
          </style>
        </head>
        <body>
          <h1>${sanitizedTitle}</h1>
          <pre>${sanitizedContent}</pre>
        </body>
      </html>
    `;
    
    // Usar Blob URL para abrir janela de impressão (mais seguro que document.write)
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, '_blank');
    
    // Aguardar carregamento e imprimir
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(blobUrl); // Limpar memória
      };
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(
        `${API_URL}/api/ai/send-email`,
        {
          title: briefing.titulo || 'Conteúdo Gerado',
          content: editableContent
        },
        { withCredentials: true }
      );
      toast.success('Conteúdo enviado para seu email!');
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar email';
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="emulador-page">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-light mb-2 flex items-center gap-3">
                <Zap className="text-amber-500" />
                Emulador Acelerador
              </h1>
              <p className="text-muted-foreground">
                Laboratório de Geração e Aceleração de Escrita
              </p>
            </div>
            
            {/* Toggle Emulator */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Label htmlFor="emulator-toggle" className="text-sm">
                {emulatorEnabled ? 'Ativado' : 'Desativado'}
              </Label>
              <Switch
                id="emulator-toggle"
                checked={emulatorEnabled}
                onCheckedChange={handleToggleEmulator}
                data-testid="emulator-toggle"
              />
            </div>
          </div>

          {/* Message */}
          {emulatorEnabled && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300">
              <Sparkles size={18} />
              <span className="text-sm">Seu Time Literário está trabalhando com você.</span>
            </div>
          )}

          {/* Time Literário Card */}
          <TimeLiterarioAtivo />

          {emulatorEnabled && (
            <div className="space-y-8">
              
              {/* SECTION 1: Entender minha voz */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-600 text-sm font-bold">1</span>
                  Entender minha voz
                </h2>
                
                <p className="text-muted-foreground mb-4">
                  Cole aqui um trecho de algo que você já escreveu e de que se orgulha, para que a IA compreenda seu estilo de escrita.
                </p>
                
                <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
                  <AlertCircle size={14} />
                  Recomendamos 3 a 5 textos. Separe-os com "---"
                </p>

                <Textarea
                  placeholder="Cole seus textos aqui... (separe múltiplos textos com ---)"
                  className="min-h-[200px] mb-4 font-serif"
                  value={writingSamples}
                  onChange={(e) => setWritingSamples(e.target.value)}
                  data-testid="writing-samples-input"
                />

                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleAnalyzeDNA}
                    disabled={analyzing || !writingSamples.trim()}
                    data-testid="analyze-dna-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Analisar DNA da Escrita
                      </>
                    )}
                  </Button>

                  {hasStyleGuide && (
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <CheckCircle2 size={18} />
                      <span className="text-sm font-medium">DNA identificado</span>
                    </div>
                  )}
                </div>
              </section>

              {/* SECTION 2: Como você quer escrever */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-600 text-sm font-bold">2</span>
                  Como você quer escrever
                </h2>

                {/* Writing Modes */}
                <div className="mb-6">
                  <Label className="mb-3 block">Modo de escrita</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {WRITING_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setSelectedMode(mode.id)}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          selectedMode === mode.id
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-border hover:border-amber-500/50'
                        }`}
                        data-testid={`mode-${mode.id}`}
                      >
                        <mode.icon size={20} className={selectedMode === mode.id ? 'text-amber-500' : 'text-muted-foreground'} />
                        <p className="font-medium mt-2">{mode.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{mode.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Intensity */}
                <div>
                  <Label className="mb-3 block">Intensidade de emulação</Label>
                  <div className="flex flex-wrap gap-3">
                    {INTENSITIES.map((intensity) => (
                      <button
                        key={intensity.id}
                        onClick={() => {
                          if (!intensity.free && !isPremium) {
                            toast.error('Disponível apenas para Premium');
                            return;
                          }
                          setSelectedIntensity(intensity.id);
                        }}
                        disabled={!intensity.free && !isPremium}
                        className={`px-4 py-3 rounded-lg border text-left transition-all relative ${
                          selectedIntensity === intensity.id
                            ? 'border-amber-500 bg-amber-500/10'
                            : intensity.free || isPremium
                            ? 'border-border hover:border-amber-500/50'
                            : 'border-border opacity-50 cursor-not-allowed'
                        }`}
                        data-testid={`intensity-${intensity.id}`}
                      >
                        {!intensity.free && !isPremium && (
                          <Crown size={14} className="absolute top-2 right-2 text-amber-500" />
                        )}
                        <p className="font-medium">{intensity.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{intensity.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              {/* SECTION 3: Gerar Conteúdo */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-600 text-sm font-bold">3</span>
                  Gerar Conteúdo
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="titulo">Título do ebook ou livro *</Label>
                    <Input
                      id="titulo"
                      placeholder="Ex: O Guia Definitivo..."
                      value={briefing.titulo}
                      onChange={(e) => setBriefing({...briefing, titulo: e.target.value})}
                      className="mt-1"
                      data-testid="briefing-titulo"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tema">Tema *</Label>
                    <Input
                      id="tema"
                      placeholder="Ex: Produtividade para escritores"
                      value={briefing.tema}
                      onChange={(e) => setBriefing({...briefing, tema: e.target.value})}
                      className="mt-1"
                      data-testid="briefing-tema"
                    />
                  </div>
                  <div>
                    <Label htmlFor="publico">Público-alvo</Label>
                    <Input
                      id="publico"
                      placeholder="Ex: Autores iniciantes"
                      value={briefing.publico_alvo}
                      onChange={(e) => setBriefing({...briefing, publico_alvo: e.target.value})}
                      className="mt-1"
                      data-testid="briefing-publico"
                    />
                  </div>
                  <div>
                    <Label htmlFor="objetivo">Objetivo da obra</Label>
                    <Input
                      id="objetivo"
                      placeholder="Ex: Ensinar técnicas práticas..."
                      value={briefing.objetivo}
                      onChange={(e) => setBriefing({...briefing, objetivo: e.target.value})}
                      className="mt-1"
                      data-testid="briefing-objetivo"
                    />
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="pontos">Pontos obrigatórios que devem aparecer</Label>
                    <Textarea
                      id="pontos"
                      placeholder="Liste os pontos que devem ser abordados..."
                      value={briefing.pontos_obrigatorios}
                      onChange={(e) => setBriefing({...briefing, pontos_obrigatorios: e.target.value})}
                      className="mt-1 min-h-[80px]"
                      data-testid="briefing-pontos"
                    />
                  </div>
                  <div>
                    <Label htmlFor="adicional">Deseja informar mais alguma coisa?</Label>
                    <Textarea
                      id="adicional"
                      placeholder="Informações adicionais, contexto, referências..."
                      value={briefing.informacoes_adicionais}
                      onChange={(e) => setBriefing({...briefing, informacoes_adicionais: e.target.value})}
                      className="mt-1 min-h-[80px]"
                      data-testid="briefing-adicional"
                    />
                  </div>
                </div>

                <Button
                  size="lg"
                  onClick={handleGenerate}
                  disabled={generating || !briefing.titulo || !briefing.tema}
                  className="w-full md:w-auto"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Gerando conteúdo...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Gerar conteúdo com IA
                    </>
                  )}
                </Button>

                {/* Button to view generated content */}
                {showResult && generatedContent && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleOpenContentModal}
                    className="w-full md:w-auto mt-3 md:mt-0 md:ml-3 border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                    data-testid="view-content-btn"
                  >
                    <Eye className="mr-2 h-5 w-5" />
                    Ver conteúdo gerado com IA
                  </Button>
                )}
              </section>

              {/* RESULT SECTION */}
              {showResult && generatedContent && (
                <section className="p-6 rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="text-green-500" />
                    Resultado
                  </h2>

                  <div className="prose prose-sm dark:prose-invert max-w-none mb-6 p-4 bg-white/80 dark:bg-gray-900/80 rounded-lg border max-h-[500px] overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed">
                      {generatedContent}
                    </pre>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      size="lg"
                      onClick={handleOpenInEditor}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="open-in-editor-btn"
                    >
                      <ChevronRight className="mr-2 h-5 w-5" />
                      Abrir no Editor do Author's Key
                    </Button>
                    
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={handleOpenContentModal}
                      data-testid="edit-view-content-btn"
                    >
                      <Edit className="mr-2 h-5 w-5" />
                      Editar / Visualizar
                    </Button>
                  </div>
                </section>
              )}

              {/* Content Modal */}
              {showContentModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                  <div className="bg-card border border-border rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-border">
                      <h2 className="text-xl font-semibold flex items-center gap-2">
                        <Sparkles className="text-amber-500" />
                        {briefing.titulo || 'Conteúdo Gerado'}
                      </h2>
                      <button 
                        onClick={() => setShowContentModal(false)}
                        className="text-muted-foreground hover:text-foreground p-2 hover:bg-muted rounded-lg transition-colors"
                        data-testid="close-content-modal"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Modal Toolbar */}
                    <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border bg-muted/30">
                      <Button
                        size="sm"
                        variant={isEditing ? "default" : "outline"}
                        onClick={() => setIsEditing(!isEditing)}
                        data-testid="toggle-edit-btn"
                      >
                        <Edit size={16} className="mr-1" />
                        {isEditing ? 'Editando' : 'Editar'}
                      </Button>
                      
                      {isEditing && (
                        <Button
                          size="sm"
                          onClick={handleSaveEditedContent}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="save-content-btn"
                        >
                          <Save size={16} className="mr-1" />
                          Salvar
                        </Button>
                      )}
                      
                      <div className="flex-1" />
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopyContent}
                        data-testid="copy-content-btn"
                      >
                        <Copy size={16} className="mr-1" />
                        Copiar
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handlePrintContent}
                        data-testid="print-content-btn"
                      >
                        <Printer size={16} className="mr-1" />
                        Imprimir
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleSendEmail}
                        disabled={sendingEmail}
                        className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
                        data-testid="send-email-btn"
                      >
                        {sendingEmail ? (
                          <Loader2 size={16} className="mr-1 animate-spin" />
                        ) : (
                          <Mail size={16} className="mr-1" />
                        )}
                        Enviar por Email
                      </Button>
                    </div>

                    {/* Modal Content */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {isEditing ? (
                        <Textarea
                          value={editableContent}
                          onChange={(e) => setEditableContent(e.target.value)}
                          className="w-full min-h-[400px] font-serif text-base leading-relaxed resize-none"
                          data-testid="content-editor"
                        />
                      ) : (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed bg-transparent p-0 m-0">
                            {editableContent}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Modal Footer */}
                    <div className="flex flex-wrap items-center gap-3 p-4 border-t border-border bg-muted/30">
                      <Button
                        onClick={handleOpenInEditor}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="modal-open-editor-btn"
                      >
                        <ChevronRight className="mr-2 h-4 w-4" />
                        Abrir no Editor do Author's Key
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => setShowContentModal(false)}
                      >
                        Fechar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Disabled State */}
          {!emulatorEnabled && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                <Zap size={40} className="text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Emulador Desativado</h2>
              <p className="text-muted-foreground mb-6">
                Ative o emulador para acessar o Laboratório de Geração e Aceleração de Escrita
              </p>
              <Button onClick={() => handleToggleEmulator(true)}>
                <Sparkles className="mr-2 h-4 w-4" />
                Ativar Emulador
              </Button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
